import * as AppCore from '@/utilities/AppCore';
import * as AppConsts from '@/utilities/AppConsts';
import { VisualFile } from '@/types/visual_file';

export interface Profile {
  profile_id: number,
  name: string,
  description: string,
  visuals: VisualFile[],
  uniqueid: string,
  instagram?: string,
  tiktok?: string,
  snapchat?: string,
  facebook?: string,
  x?: string,
  website?: string,
  optionalfilled?: boolean,
  children_have?: number | null,
  children_want?: number | null,
  educationlevel?: number | null,
  job_name?: string | null,
  job_title?: string | null,
  sexualorientation?: number | null,
  pronouns?: string | null,
  pets?: number | null,
  drink?: number | null,
  smoke?: number | null,
  weed?: number | null,
  drugs?: number | null,
  exercise?: number | null,
  activeonsocial?: number | null,
  lookingfor?: number | null,
  relationshiptype?: number | null,
  languages?: string | null,
  interests?: number | null,
  status?: string | null,
  quote?: string | null,
  interests_general?: string | null,
  interests_music?: string | null,
  interests_movies?: string | null,
  interests_games?: string | null,
  interests_books?: string | null,
  interests_groups?: string | null,
  desiredqualities?: number | null,
  ethnicity?: number | null,
  politics?: number | null,
  religion?: number | null,
}

export const UNKNOWN_PROFILE : Profile = {
  profile_id: -1,
  name: "Unknown",
  description: "Unknown",
  visuals: [],
  uniqueid: "unknown",
};

export enum Ethnicity {
  ETHNICITY_NONE = 0,
  ETHNICITY_WHITE = 1,
  ETHNICITY_BLACK = 2,
  ETHNICITY_EAST_ASIAN = 4,
  ETHNICITY_HISPANIC = 8,
  ETHNICITY_MIDDLE_EASTERN = 16,
  ETHNICITY_NATIVE_AMERICAN = 32,
  ETHNICITY_PACIFIC_ISLANDER = 64,
  ETHNICITY_SOUTH_ASIAN = 128,
  ETHNICITY_SOUTHEAST_ASIAN = 256,
  ETHNICITY_OTHER = 512,
};

export const EthnicityNames : Map<number, string> = new Map([
  [Ethnicity.ETHNICITY_NONE, AppConsts.NO_ANSWER],
  [Ethnicity.ETHNICITY_WHITE, "White/Caucasian"],
  [Ethnicity.ETHNICITY_BLACK, "Black/African Descent"],
  [Ethnicity.ETHNICITY_EAST_ASIAN, "East Asian"],
  [Ethnicity.ETHNICITY_HISPANIC, "Hispanic/Latino"],
  [Ethnicity.ETHNICITY_MIDDLE_EASTERN, "Middle Eastern"],
  [Ethnicity.ETHNICITY_NATIVE_AMERICAN, "Native American"],
  [Ethnicity.ETHNICITY_PACIFIC_ISLANDER, "Pacific Islander"],
  [Ethnicity.ETHNICITY_SOUTH_ASIAN, "South Asian"],
  [Ethnicity.ETHNICITY_SOUTHEAST_ASIAN, "Southeast Asian"],
  [Ethnicity.ETHNICITY_OTHER, "Other"],
]);

export function getEthnicities(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.ethnicity, Ethnicity, EthnicityNames);
}

export enum Religion {
  RELIGION_NONE = 0,
  RELIGION_AGNOSTIC = 1,
  RELIGION_ATHEIST = 2,
  RELIGION_BUDDHIST = 4,
  RELIGION_CATHOLIC = 8,
  RELIGION_CHRISTIAN = 16,
  RELIGION_HINDU = 32,
  RELIGION_JEWISH = 64,
  RELIGION_MUSLIM = 128,
  RELIGION_SIKH = 256,
  RELIGION_SPIRITUAL = 512,
  RELIGION_OTHER = 1024,
};

export const ReligionNames : Map<number, string> = new Map([
  [Religion.RELIGION_NONE, AppConsts.NO_ANSWER],
  [Religion.RELIGION_AGNOSTIC, "Agnostic"],
  [Religion.RELIGION_ATHEIST, "Atheist"],
  [Religion.RELIGION_BUDDHIST, "Buddhist"],
  [Religion.RELIGION_CATHOLIC, "Catholic"],
  [Religion.RELIGION_CHRISTIAN, "Christian"],
  [Religion.RELIGION_HINDU, "Hindu"],
  [Religion.RELIGION_JEWISH, "Jewish"],
  [Religion.RELIGION_MUSLIM, "Muslim"],
  [Religion.RELIGION_SIKH, "Sikh"],
  [Religion.RELIGION_SPIRITUAL, "Spiritual"],
  [Religion.RELIGION_OTHER, "Other"],
]);

export function getReligions(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.religion, Religion, ReligionNames);
}

export enum Pronoun {
  PRONOUN_NONE = 0,
  PRONOUN_SHE = 1,
  PRONOUN_HER = 2,
  PRONOUN_HERS = 4,
  PRONOUN_HE = 8,
  PRONOUN_HIM = 16,
  PRONOUN_HIS = 32,
  PRONOUN_THEY = 64,
  PRONOUN_THEM = 128,
  PRONOUN_THEIRS = 256,
  PRONOUN_VE = 512,
  PRONOUN_VER = 1024,
  PRONOUN_VIS = 2048,
  PRONOUN_ZE = 4096,
  PRONOUN_HIR = 8192,
  PRONOUN_HIRS = 16384,
  PRONOUN_AE = 32768,
  PRONOUN_AER = 65536,
  PRONOUN_AERS = 131072,
  PRONOUN_EY = 262144,
  PRONOUN_EM = 524288,
  PRONOUN_EIRS = 1048576,
  PRONOUN_FAE = 2097152,
  PRONOUN_FAER = 4194304,
  PRONOUN_FAERS = 8388608,
  PRONOUN_XE = 16777216,
  PRONOUN_XEM = 33554432,
  PRONOUN_XYRS = 67108864,
  PRONOUN_ZIR = 134217728,
  PRONOUN_ZIRS = 268435456,
  PRONOUN_NOT_LISTED = 536870912,
};

export const PronounNames : Map<number, string> = new Map([
  [Pronoun.PRONOUN_NONE, AppConsts.NO_ANSWER],
  [Pronoun.PRONOUN_SHE, "she"],
  [Pronoun.PRONOUN_HER, "her"],
  [Pronoun.PRONOUN_HERS, "hers"],
  [Pronoun.PRONOUN_HE, "he"],
  [Pronoun.PRONOUN_HIM, "him"],
  [Pronoun.PRONOUN_HIS, "his"],
  [Pronoun.PRONOUN_THEY, "they"],
  [Pronoun.PRONOUN_THEM, "them"],
  [Pronoun.PRONOUN_THEIRS, "theirs"],
  [Pronoun.PRONOUN_VE, "ve"],
  [Pronoun.PRONOUN_VER, "ver"],
  [Pronoun.PRONOUN_VIS, "vis"],
  [Pronoun.PRONOUN_ZE, "ze"],
  [Pronoun.PRONOUN_HIR, "hir"],
  [Pronoun.PRONOUN_HIRS, "hirs"],
  [Pronoun.PRONOUN_AE, "ae"],
  [Pronoun.PRONOUN_AER, "aer"],
  [Pronoun.PRONOUN_AERS, "aers"],
  [Pronoun.PRONOUN_EY, "ey"],
  [Pronoun.PRONOUN_EM, "em"],
  [Pronoun.PRONOUN_EIRS, "eirs"],
  [Pronoun.PRONOUN_FAE, "fae"],
  [Pronoun.PRONOUN_FAER, "faer"],
  [Pronoun.PRONOUN_FAERS, "faers"],
  [Pronoun.PRONOUN_XE, "xe"],
  [Pronoun.PRONOUN_XEM, "xem"],
  [Pronoun.PRONOUN_XYRS, "xyrs"],
  [Pronoun.PRONOUN_ZIR, "zir"],
  [Pronoun.PRONOUN_ZIRS, "zirs"],
  [Pronoun.PRONOUN_NOT_LISTED, "Not listed"],
]);

export function getPronouns(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.religion, Pronoun, PronounNames);
}

export enum Sexuality {
  SEXUALITY_NONE = 0,
  SEXUALITY_STRAIGHT = 1,
  SEXUALITY_LESBIAN = 2,
  SEXUALITY_BISEXUAL = 4,
  SEXUALITY_ALLOSEXUAL = 8,
  SEXUALITY_ANDROSEXUAL = 16,
  SEXUALITY_ASEXUAL = 32,
  SEXUALITY_AUTOSEXUAL = 64,
  SEXUALITY_BICURIOUS = 128,
  SEXUALITY_DEMISEXUAL = 256,
  SEXUALITY_FLUID = 512,
  SEXUALITY_GRAYSEXUAL = 1024,
  SEXUALITY_GYNESEXUAL = 2048,
  SEXUALITY_MONOSEXUAL = 4096,
  SEXUALITY_OMNISEXUAL = 8192,
  SEXUALITY_PANSEXUAL = 16384,
  SEXUALITY_POLYSEXUAL = 32768,
  SEXUALITY_QUEER = 65536,
  SEXUALITY_QUESTIONING = 131072,
  SEXUALITY_SKOLOSEXUAL = 262144,
  SEXUALITY_SPECTRASEXUAL = 524288,
  SEXUALITY_GAY = 1048576,
  SEXUALITY_NOT_LISTED = 2097152,
};

export const SexualityNames : Map<number, string> = new Map([
  [Sexuality.SEXUALITY_NONE, AppConsts.NO_ANSWER],
  [Sexuality.SEXUALITY_STRAIGHT, "Straight"],
  [Sexuality.SEXUALITY_LESBIAN, "Lesbian"],
  [Sexuality.SEXUALITY_BISEXUAL, "Bisexual"],
  [Sexuality.SEXUALITY_ALLOSEXUAL, "Allosexual"],
  [Sexuality.SEXUALITY_ANDROSEXUAL, "Androsexual"],
  [Sexuality.SEXUALITY_ASEXUAL, "Asexual"],
  [Sexuality.SEXUALITY_AUTOSEXUAL, "Autosexual"],
  [Sexuality.SEXUALITY_BICURIOUS, "Bicurious"],
  [Sexuality.SEXUALITY_DEMISEXUAL, "Demisexual"],
  [Sexuality.SEXUALITY_FLUID, "Fluid"],
  [Sexuality.SEXUALITY_GRAYSEXUAL, "Graysexual"],
  [Sexuality.SEXUALITY_GYNESEXUAL, "Gynesexual"],
  [Sexuality.SEXUALITY_MONOSEXUAL, "Monosexual"],
  [Sexuality.SEXUALITY_OMNISEXUAL, "Omnisexual"],
  [Sexuality.SEXUALITY_PANSEXUAL, "Pansexual"],
  [Sexuality.SEXUALITY_POLYSEXUAL, "Polysexual"],
  [Sexuality.SEXUALITY_QUEER, "Queer"],
  [Sexuality.SEXUALITY_QUESTIONING, "Questioning"],
  [Sexuality.SEXUALITY_SKOLOSEXUAL, "Skolosexual"],
  [Sexuality.SEXUALITY_SPECTRASEXUAL, "Spectrasexual"],
  [Sexuality.SEXUALITY_GAY, "Gay"],
  [Sexuality.SEXUALITY_NOT_LISTED, "Not listed"],
]);

export function getSexualities(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.sexualorientation, Sexuality, SexualityNames);
}

export enum Children {
  CHILDREN_NONE = 0,
  CHILDREN_HAVE_CHILDREN = 1,
  CHILDREN_DO_NOT_HAVE_CHILDREN = 2,
};

export const ChildrenNames : Map<number, string> = new Map([
  [Children.CHILDREN_NONE, AppConsts.NO_ANSWER],
  [Children.CHILDREN_HAVE_CHILDREN, "Have children"],
  [Children.CHILDREN_DO_NOT_HAVE_CHILDREN, "Don't have children"],
]);

export function getChildrenStatus(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.children_have, Children, ChildrenNames);
}

export enum FamilyPlan {
  FAMILY_PLAN_NONE = 0,
  FAMILY_PLAN_WANT_CHILDREN = 1,
  FAMILY_PLAN_OPEN_TO_CHILDREN = 2,
  FAMILY_PLAN_DO_NOT_WANT_CHILDREN = 4,
  FAMILY_PLAN_NOT_SURE_YET = 8,
};

export const FamilyPlanNames : Map<number, string> = new Map([
  [FamilyPlan.FAMILY_PLAN_NONE, AppConsts.NO_ANSWER],
  [FamilyPlan.FAMILY_PLAN_WANT_CHILDREN, "Want children"],
  [FamilyPlan.FAMILY_PLAN_OPEN_TO_CHILDREN, "Open to children"],
  [FamilyPlan.FAMILY_PLAN_DO_NOT_WANT_CHILDREN, "Don't want children"],
  [FamilyPlan.FAMILY_PLAN_NOT_SURE_YET, "Not sure yet"],
]);

export function getFamilyPlanStatus(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.children_want, FamilyPlan, FamilyPlanNames);
}

export enum Pet {
  PET_NONE = 0,
  PET_DOG = 1,
  PET_CAT = 2,
  PET_BIRD = 4,
  PET_FISH = 8,
  PET_REPTILE = 16,
};

export const PetNames : Map<number, string> = new Map([
  [Pet.PET_NONE, AppConsts.NO_ANSWER],
  [Pet.PET_DOG, "Dogs"],
  [Pet.PET_CAT, "Cats"],
  [Pet.PET_BIRD, "Birds"],
  [Pet.PET_FISH, "Fish"],
  [Pet.PET_REPTILE, "Reptiles"],
]);

export function getPets(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.children_want, FamilyPlan, FamilyPlanNames);
}

export enum Education {
  EDUCATION_NONE = 0,
  EDUCATION_HIGH_SCHOOL = 1,
  EDUCATION_BACHELORS_DEGREE = 2,
  EDUCATION_GRADUATE_DEGREE = 4,
  EDUCATION_PHD = 8,
};

export const EducationNames : Map<number, string> = new Map([
  [Education.EDUCATION_NONE, AppConsts.NO_ANSWER],
  [Education.EDUCATION_HIGH_SCHOOL, "High school"],
  [Education.EDUCATION_BACHELORS_DEGREE, "Bachelor's Degree"],
  [Education.EDUCATION_GRADUATE_DEGREE, "Graduate Degree"],
  [Education.EDUCATION_PHD, "PhD"],
]);

export function getEducations(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.educationlevel, Education, EducationNames);
}

export enum Politics {
  POLITICS_NONE = 0,
  POLITICS_LIBERAL = 1,
  POLITICS_MODERATE = 2,
  POLITICS_CONSERVATIVE = 4,
  POLITICS_NOT_POLITICAL = 8,
  POLITICS_OTHER = 16,
};

export const PoliticsNames : Map<number, string> = new Map([
  [Politics.POLITICS_NONE, AppConsts.NO_ANSWER],
  [Politics.POLITICS_LIBERAL, "Liberal"],
  [Politics.POLITICS_MODERATE, "Moderate"],
  [Politics.POLITICS_CONSERVATIVE, "Conservative"],
  [Politics.POLITICS_NOT_POLITICAL, "Not political"],
  [Politics.POLITICS_OTHER, "Other"],
]);

export function getPolitics(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.politics, Politics, PoliticsNames);
}

export enum Intention {
  INTENTION_NONE = 0,
  INTENTION_LIFE_PARTNER = 1,
  INTENTION_LONG = 2,
  INTENTION_LONG_OPEN_TO_SHORT = 4,
  INTENTION_SHORT_OPEN_TO_LONG = 8,
  INTENTION_SHORT = 16,
  INTENTION_FIGURING_OUT = 32,
};

export const IntentionNames : Map<number, string> = new Map([
  [Intention.INTENTION_NONE, AppConsts.NO_ANSWER],
  [Intention.INTENTION_LIFE_PARTNER, "Life partner"],
  [Intention.INTENTION_LONG, "Long-term relationship"],
  [Intention.INTENTION_LONG_OPEN_TO_SHORT, "Long-term relationship, open to short"],
  [Intention.INTENTION_SHORT_OPEN_TO_LONG, "Short-term relationship, open to long"],
  [Intention.INTENTION_SHORT, "Short-term relationship"],
  [Intention.INTENTION_FIGURING_OUT, "Figuring out my dating goals"],
]);

export function getIntentions(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.lookingfor, Intention, IntentionNames);
}

export enum RelationshipType {
  RELATIONSHIP_TYPE_NONE = 0,
  RELATIONSHIP_TYPE_MONOGAMY = 1,
  RELATIONSHIP_TYPE_NON_MONOGAMY = 2,
  RELATIONSHIP_TYPE_FIGURING_OUT = 4,
};

export const RelationshipTypeNames : Map<number, string> = new Map([
  [RelationshipType.RELATIONSHIP_TYPE_NONE, AppConsts.NO_ANSWER],
  [RelationshipType.RELATIONSHIP_TYPE_MONOGAMY, "Monogamy"],
  [RelationshipType.RELATIONSHIP_TYPE_NON_MONOGAMY, "Non-monogamy"],
  [RelationshipType.RELATIONSHIP_TYPE_FIGURING_OUT, "Figuring out my relationship type"],
]);

export function getRelationshipTypes(profile: Profile) : string {
  return AppCore.getEnumValuesJoined(profile.relationshiptype, RelationshipType, RelationshipTypeNames);
}

export enum Frequency {
  FREQUENCY_NONE = 0,
  FREQUENCY_NO = 1,
  FREQUENCY_SOMETIMES = 2,
  FREQUENCY_YES = 4,
};

export const FrequencyNames : Map<number, string> = new Map([
  [Frequency.FREQUENCY_NONE, AppConsts.NO_ANSWER],
  [Frequency.FREQUENCY_NO, "No"],
  [Frequency.FREQUENCY_SOMETIMES, "Sometimes"],
  [Frequency.FREQUENCY_YES, "Yes"],
]);

export function getFrequencyTypes(bits: number | undefined | null) : string {
  return AppCore.getEnumValuesJoined(bits, Frequency, FrequencyNames);
}
